import * as MetkaClient from './api-client/metka-client'
import { customFetch } from './api-client/mutator'
import { ensureOk } from '@/lib/errors'

// Notifications list DTO — the swagger-generated `getApiV1NotificationsResponse`
// has `data: void` because the endpoint response type is not surfaced in OpenAPI.
// Keep a local type so callers have real field completion.
export interface NotificationItem {
    id: string
    kind: string
    title: string
    message: string
    level?: string | null
    context?: Record<string, unknown> | null
    isRead: boolean
    createdAt: string
    accountId?: string | null
    accountName?: string | null
}
export interface NotificationsList {
    items: NotificationItem[]
    unreadCount: number
}

// Types for batch delete acts (until Orval client is regenerated)
export interface BatchDeleteActResult {
    externalId: string
    success: boolean
    error?: string
}

export interface BatchDeleteActsResponse {
    results: BatchDeleteActResult[]
}

// Types for compliance report with provider breakdown
export interface ProviderBreakdown {
    provider: string
    total: number
    submittedCount: number
    withActsCount: number
    withIssuesCount: number
}

export interface ComplianceReportResponse {
    items: Array<MetkaClient.ComplianceRow & { provider?: string }>
    total: number
    /** Размер набора после stage-фильтра — для пагинации; без фильтра равен total */
    filteredTotal?: number
    page: number
    pageSize: number
    submittedCount: number
    overdueCount: number
    actionRequiredCount: number
    readyToSubmitCount: number
    providerBreakdowns: ProviderBreakdown[]
    ordLoadErrors?: string[]
    /** Креативы, по которым отчётность завершилась в этом месяце (диалог при входе) */
    reportingFinishedThisMonth?: ReportingFinishedItem[]
}

export interface ReportingFinishedItem {
    erid: string
    creativeName?: string | null
    /** Последний месяц с активностью (YYYY-MM) */
    lastActivityMonth?: string | null
    /** Месяц полного истечения обязанности (YYYY-MM) */
    obligationEndsAt?: string | null
}

// Обёртка над сгенерированным клиентом с авторизацией и обработкой ошибок
class ApiClient {
    private async getAuthHeaders(): Promise<Record<string, string>> {
        // На сервере можем прокинуть Bearer, на клиенте — полагаемся на BFF (/api/v1/*) и cookie сессии
        if (typeof window === 'undefined') {
            try {
                const { auth } = await import('@/auth')
                const session = await auth()
                const bearer = (session as any)?.accessToken as string | undefined
                if (bearer) {
                    return {
                        'Authorization': `Bearer ${bearer}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    }
                }
            } catch { /* ignore, вернём базовые заголовки */ }
        }
        return {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }
    }

    /**
     * Нормализация адреса контрагента под формат ЕРИР (Ozon).
     * Возвращает предложение — метод фаззи-матчит и может подменить номер дома,
     * поэтому применять результат можно только с подтверждения пользователя.
     */
    async correctAddress(address: string, provider?: string, opts?: { signal?: AbortSignal }) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdAddressCorrect({ address, provider }, { ...options, signal: opts?.signal })
        )
    }

    // Compliance API
    async getComplianceReport(params: { month: string; actionRequired?: boolean; stage?: string; page?: number; pageSize?: number; providers?: string[]; hideCompleted?: boolean; erids?: string[]; search?: string }): Promise<ComplianceReportResponse> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ComplianceReport({
                month: params.month,
                actionRequired: params.actionRequired,
                stage: params.stage,
                page: params.page,
                pageSize: params.pageSize,
                providers: params.providers?.join(','),
                hideCompleted: params.hideCompleted,
                erids: params.erids?.length ? params.erids.join(',') : undefined,
                search: params.search || undefined,
            }, options)
        )
    }
    async getComplianceSummary(months?: number, opts?: { signal?: AbortSignal }): Promise<MetkaClient.ComplianceSummaryResponse> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ComplianceSummary({ months }, { ...options, signal: opts?.signal })
        )
    }
    async createComplianceAct(data: MetkaClient.ActCreateRequest, idempotencyKey?: string) {
        const baseHeaders = await this.getAuthHeaders()
        const headers: Record<string, string> = { ...baseHeaders }
        headers['Content-Type'] = 'application/json'
        headers['Accept'] = 'application/json'
        headers['X-Idempotency-Key'] = idempotencyKey && idempotencyKey.length > 0
            ? idempotencyKey
            : `act_${Date.now()}_${Math.random().toString(36).slice(2)}`
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceActs(data, { ...(options ?? {}), headers })
        )
    }
    async submitComplianceBatch(data: { creativeIds: string[]; forceResubmit?: boolean; month: string }, idempotencyKey?: string) {
        const baseHeaders = await this.getAuthHeaders()
        const headers: Record<string, string> = { ...baseHeaders }
        headers['Content-Type'] = 'application/json'
        headers['Accept'] = 'application/json'
        headers['X-Idempotency-Key'] = idempotencyKey && idempotencyKey.length > 0
            ? idempotencyKey
            : `batch_${Date.now()}_${Math.random().toString(36).slice(2)}`
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceReportSubmitBatch({
                creativeIds: data.creativeIds as any,
                forceResubmit: data.forceResubmit,
                month: data.month
            }, { ...(options ?? {}), headers })
        )
    }
    async collectComplianceStats(data: MetkaClient.SimpleRequestByCreative) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceStatsCollect(data, options)
        )
    }
    async resendComplianceOrd(data: MetkaClient.SimpleRequestByCreative) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceOrdResend(data, options)
        )
    }
    async linkInitialContract(data: MetkaClient.LinkInitialContractRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceInitialContractLink(data, options)
        )
    }
    async assignPadToCreative(data: { erid: string; padId: string }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceAssignPad(data as MetkaClient.AssignPadRequest, options)
        )
    }
    async assignPadsToCreative(data: { erid: string; padIds: string[] }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceAssignPads(data as MetkaClient.AssignPadsRequest, options)
        )
    }
    async assignPlacementPeriod(data: { erid: string; startDateMs: number; endDateMs: number }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceAssignPlacementPeriod(data as MetkaClient.AssignPlacementPeriodRequest, options)
        )
    }
    async verifyTelegramErid(creativeId: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceTelegramVerify({ creativeId }, options as any)
        )
    }
    async updateManualImpressions(data: { creativeId: string; impressions: number; year: number; month: number }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ComplianceImpressionsManual(data as MetkaClient.ManualImpressionsRequest, options)
        )
    }

    // Notifications preferences
    async getNotificationPreferences() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1NotificationsPreferences(options)
        )
    }
    async updateNotificationPreferences(data: { channels?: { email: boolean; push: boolean; telegram?: boolean }; frequency?: 'Weekly' | 'CriticalOnly' | 'Off'; typesEnabled?: string[] }) {
        return this.makeRequest(options =>
            MetkaClient.putApiV1NotificationsPreferences(data as MetkaClient.PreferencesPutRequest, options)
        )
    }

    // Notifications preferences V2 (matrix-based settings)
    async getNotificationPreferencesV2() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1NotificationsPreferencesV2(options)
        )
    }
    async updateNotificationPreferencesV2(data: MetkaClient.UpdateNotificationPreferencesV2Request) {
        return this.makeRequest(options =>
            MetkaClient.putApiV1NotificationsPreferencesV2(data, options)
        )
    }

    // Telegram Notifications API
    async connectTelegramNotifications(): Promise<{ deepLink: string; expiresAt: string }> {
        return this.makeRequest(options =>
            MetkaClient.initiateTelegramConnection(options)
        ) as Promise<{ deepLink: string; expiresAt: string }>
    }

    async getTelegramNotificationStatus(): Promise<{ connected: boolean; username?: string; connectedAt?: string; lastError?: string }> {
        return this.makeRequest(options =>
            MetkaClient.getTelegramStatus(options)
        ) as Promise<{ connected: boolean; username?: string; connectedAt?: string; lastError?: string }>
    }

    async disconnectTelegramNotifications(): Promise<{ success: boolean; message?: string }> {
        return this.makeRequest(options =>
            MetkaClient.disconnectTelegram(options)
        ) as Promise<{ success: boolean; message?: string }>
    }

    // Video watermark (burn-in ERID marker).
    async enqueueVideoWatermark(
        postId: string,
        body: MetkaClient.VideoWatermarkRequest,
        opts?: { retry?: boolean }
    ): Promise<MetkaClient.VideoWatermarkEnqueueResponse> {
        return this.makeRequest(options =>
            opts?.retry
                ? MetkaClient.postApiV1PostsIdVideoWatermarkRetry(postId, body, options)
                : MetkaClient.postApiV1PostsIdVideoWatermark(postId, body, options)
        ) as Promise<MetkaClient.VideoWatermarkEnqueueResponse>
    }

    async getVideoWatermarkStatus(postId: string, signal?: AbortSignal): Promise<MetkaClient.VideoWatermarkStatusDto | null> {
        try {
            const data = await this.makeRequest(options =>
                MetkaClient.getApiV1PostsIdVideoWatermark(postId, { ...options, signal })
            )
            return (data as MetkaClient.VideoWatermarkStatusDto | null) ?? null
        } catch (e: any) {
            // 204 No Content — nothing in pipeline yet.
            if (e?.status === 204) return null
            throw e
        }
    }

    async sendPublicationToTelegram(postId: string): Promise<{ ok: boolean; message: string }> {
        const headers = await this.getAuthHeaders()
        const resp = await fetch(`/api/v1/telegram/send-publication/${encodeURIComponent(postId)}`, {
            method: 'POST',
            headers,
        })
        ensureOk(resp)
        return resp.json()
    }

    // Export API
    async startComplianceExport(req: { month: string; groupBy?: string; columnsPreset?: string; columns?: string[]; onlyActionRequired?: boolean }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ExportCompliance(req as MetkaClient.ExportStartRequest, options)
        ) as Promise<{ exportId: string }>
    }
    async getExportStatus(exportId: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ExportExportId(exportId, options)
        ) as Promise<{ status: 'pending' | 'processing' | 'done' | 'error'; preSignedUrl?: string | null; sizeBytes?: number | null; error?: string | null }>
    }

    // Admin: список членств юзера в кабинетах. Используется в админ-таблице
    // юзеров для выбора кабинета при impersonate. Не в Orval — добавим при regen.
    async getAdminUserMemberships(userId: string) {
        const headers = await this.getAuthHeaders()
        const resp = await fetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/memberships`, {
            method: 'GET',
            headers,
            credentials: 'include',
        })
        if (!resp.ok) {
            const text = await resp.text().catch(() => '')
            throw new Error(`getAdminUserMemberships failed: ${resp.status} ${text}`)
        }
        return await resp.json() as Array<{
            accountId: string
            name: string
            teamName?: string | null
            ownerUserId: string
            organizationId: string
            roleId: string
        }>
    }

    private async makeRequest<T>(
        requestFn: (options?: RequestInit) => Promise<T>
    ): Promise<any> {
        try {
            const headers = await this.getAuthHeaders()
            const response = await requestFn({ headers })
            const maybeWrapped = response as any
            // Обрабатываем только orval-обёртку { status: number, data: any }
            if (
                maybeWrapped &&
                typeof maybeWrapped === 'object' &&
                'status' in maybeWrapped &&
                'data' in maybeWrapped &&
                typeof (maybeWrapped as { status: unknown }).status === 'number'
            ) {
                const status = (maybeWrapped as { status: number }).status
                const data = (maybeWrapped as { data?: unknown }).data
                if (status >= 200 && status < 300) {
                    return data
                }

                let message = 'Произошла ошибка запроса'
                let parsedData = data
                try {
                    if (data && typeof data === 'object') {
                        const obj = data as any
                        message = obj.detail || obj.title || obj.message || obj.error || obj.code || obj.reason || message
                    } else if (typeof data === 'string') {
                        const trimmed = data.trim()
                        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                            parsedData = JSON.parse(trimmed)
                            const obj = parsedData as any
                            message = obj.detail || obj.title || obj.message || obj.error || obj.code || obj.reason || message
                        } else if (trimmed.length > 0) {
                            message = trimmed
                        }
                    }
                } catch { /* keep default message */ }

                // Throw error object with status and data for proper error handling
                const apiError = { status, data: parsedData, message }
                throw apiError
            }
            return response
        } catch (error) {
            console.error('API request failed:', error)
            throw error
        }
    }

    // Posts API
    async getPosts(params?: {
        page?: number
        size?: number
        source?: string
        q?: string
        isAd?: boolean
        hasErid?: boolean
        erid?: string
        dateFrom?: string
        dateTo?: string
        providers?: string
    }) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Posts(params, options)
        )
    }

    async getPost(id: string, opts?: { signal?: AbortSignal }) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1PostsId(id, { ...options, signal: opts?.signal })
        )
    }

    async getPostsByErid(erid: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1PostsByErid({ erid }, options)
        )
    }

    async createDraftPost(data: MetkaClient.CreateDraftRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1PostsDraft(data, options)
        )
    }

    async updatePost(id: string | number, data: { contentText?: string; draftStateJson?: string; contentTextMarked?: string; markedTextIndex?: number; adPeriodStartMs?: number; adPeriodEndMs?: number; images?: string[] }) {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }

            // Копируем Authorization из options если есть
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) {
                    headers['Authorization'] = authHeader
                }
            }

            const response = await fetch(`/api/v1/posts/${String(id)}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(data),
            })
            await ensureOk(response)
            return response.json()
        })
    }

    async deletePost(id: string | number) {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1PostsId(String(id), options)
        )
    }

    // Analysis API
    async getAnalysis(params?: {
        limit?: number
        beforeId?: number
        workflow?: string
        author?: string
        isAd?: boolean
        hasErid?: boolean
    }) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Analysis(params, options)
        )
    }

    async getAnalysisFeed(params?: {
        limit?: number
        beforeId?: number
        workflow?: string
        author?: string
        isAd?: boolean
        hasErid?: boolean
        erid?: string
        dateFrom?: string
        dateTo?: string
    }) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1AnalysisFeed(params, options)
        )
    }

    async getAnalysisById(id: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1AnalysisId(id, options)
        )
    }

    async updateAnalysis(id: string, data: { comment?: string; [key: string]: any }) {
        return this.makeRequest(options =>
            MetkaClient.patchApiV1AnalysisId(id, data as MetkaClient.PatchApiV1AnalysisIdBody, options)
        )
    }

    // Notifications API
    async getNotifications(params?: {
        unread?: boolean
        limit?: number
        accountId?: string
    }): Promise<NotificationsList> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Notifications(params, options)
        ) as unknown as Promise<NotificationsList>
    }

    async markNotificationsRead(data: MetkaClient.ReadReq) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1NotificationsRead(data, options)
        )
    }

    async getNotificationsStream() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1NotificationsStream(options)
        )
    }

    // Stats API
    async getStats() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Stats(options)
        )
    }

    // Onboarding API
    async getOnboardingState() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OnboardingState(options)
        )
    }

    async updateOnboardingState(data: MetkaClient.OnboardingStateUpdateReq) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OnboardingState(data, options)
        )
    }

    async createUserOnboarding(data: MetkaClient.OnboardingPayload, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1UsersOnboarding(data, provider ? { provider } : undefined, options)
        )
    }

    async createUserOnboardingSelfPromotion(data: any, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1UsersOnboardingSelfPromotion(data, provider ? { provider } : undefined, options)
        )
    }

    // KKTU Dictionary API
    async getKktu(params?: {
        search?: string
        limit?: number
        lang?: string
        offset?: number
        codes?: string
        leafOnly?: boolean
    }) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1DictKktu(params, options)
        )
    }

    async getKktuCount() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1DictKktuCount(options)
        )
    }

    async bulkUpsertKktu(data: MetkaClient.BulkUpsertKktuRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1DictKktuBulk(data, options)
        )
    }

    async seedKktu(data: MetkaClient.SeedKktuRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1DictKktuSeed(data, options)
        )
    }

    async clearKktu() {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1DictKktu(options)
        )
    }

    // Channels API
    async getChannels() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Channels(options)
        )
    }

    async connectChannel(data: MetkaClient.ConnectChannelRequest, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ChannelsConnect(data, provider ? { provider } : undefined, options)
        )
    }

    // Pads API
    async getPads(params?: MetkaClient.GetApiV1PadsParams) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Pads(params, options)
        )
    }

    async getPadsReadiness(ids: string[]): Promise<MetkaClient.PadReadinessResponse> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1PadsReadiness({ ids }, options)
        )
    }

    async getPadById(id: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1PadsId(id, options)
        )
    }

    async createPad(data: MetkaClient.CreatePadRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1Pads(data, options)
        )
    }

    async updatePad(id: string, data: MetkaClient.UpdatePadRequest) {
        return this.makeRequest(options =>
            MetkaClient.putApiV1PadsId(id, data, options)
        )
    }

    async deletePad(id: string) {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1PadsId(id, options)
        )
    }

    async syncPadsFromOrd(provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1PadsSyncFromOrd({ provider }, options)
        )
    }

    async resolvePlatform(data: MetkaClient.ResolvePlatformRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1PadsResolve(data, options)
        )
    }

    async connectPadTelegram(id: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1PadsIdTelegramConnect(id, options)
        )
    }

    async deleteChannel(id: string) {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1ChannelsId(id, options)
        )
    }

    // Contracts API
    async getContracts(params?: { offset?: number; limit?: number; provider?: string }) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdContractsList(params, options)
        )
    }

    async getContract(externalId: string, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.getContractByExternalId(externalId, provider ? { provider } : undefined, options)
        )
    }

    async getOrdContracts() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdContracts(options)
        )
    }

    async createContract(data: MetkaClient.CreateContractLocalRequest, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ContractsCreate(data, provider ? { provider } : undefined, options)
        )
    }

    // Обновление договора использует тот же backend-эндпоинт (upsert через VK ORD PUT)
    async updateContract(data: MetkaClient.CreateContractLocalRequest, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ContractsCreate(data, provider ? { provider } : undefined, options)
        )
    }

    async createContractCid(externalId: string, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ContractsExternalIdCreateCid(externalId, provider ? { provider } : undefined, options)
        )
    }

    async storeContract(data: MetkaClient.StoreContractReq) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ContractsStore(data, options)
        )
    }

    async getContractsList() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ContractsList(options)
        )
    }

    // Contract Addendums API
    async getContractAddendums(contractId: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ContractsContractIdAddendums(contractId, options)
        )
    }

    async createContractAddendum(contractId: string, data: MetkaClient.AddendumCreateRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ContractsContractIdAddendums(contractId, data, options)
        )
    }

    async updateContractAddendum(contractId: string, id: string, data: MetkaClient.AddendumUpdateRequest) {
        return this.makeRequest(options =>
            MetkaClient.putApiV1ContractsContractIdAddendumsId(contractId, id, data, options)
        )
    }

    async deleteContractAddendum(contractId: string, id: string) {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1ContractsContractIdAddendumsId(contractId, id, options)
        )
    }

    // Compliance — placement and monthly stats (fallback fetch until client is regenerated)
    async getPlacementStatus(erids: string[]) {
        const headers = await this.getAuthHeaders()
        const qs = new URLSearchParams()
        if (erids && erids.length > 0) qs.set('erids', erids.join(','))
        const resp = await fetch(`/api/v1/compliance/placement/status?${qs.toString()}`, {
            method: 'GET',
            headers,
            credentials: 'include',
        })
        await ensureOk(resp)
        return resp.json()
    }

    async getMonthlyStatsByErid(erid: string) {
        const headers = await this.getAuthHeaders()
        const resp = await fetch(`/api/v1/compliance/statistics/by-erid/${encodeURIComponent(erid)}`, {
            method: 'GET',
            headers,
            credentials: 'include',
        })
        await ensureOk(resp)
        return resp.json()
    }
    async getPlacementStatusByCreative(creativeIds: string[]) {
        const headers = await this.getAuthHeaders()
        const qs = new URLSearchParams()
        if (creativeIds && creativeIds.length > 0) qs.set('creativeIds', creativeIds.join(','))
        const resp = await fetch(`/api/v1/compliance/placement/status?${qs.toString()}`, {
            method: 'GET',
            headers,
            credentials: 'include',
        })
        await ensureOk(resp)
        return resp.json()
    }
    async getMonthlyStatsByCreative(creativeId: string) {
        const headers = await this.getAuthHeaders()
        const resp = await fetch(`/api/v1/compliance/statistics/by-creative/${encodeURIComponent(creativeId)}`, {
            method: 'GET',
            headers,
            credentials: 'include',
        })
        await ensureOk(resp)
        return resp.json()
    }

    // Profile API
    async getProfileContract() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ProfileContract(options)
        )
    }

    async updateProfileContract(data: MetkaClient.SetSelectedContractReq) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ProfileContract(data, options)
        )
    }

    async getProfileKktu() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ProfileKktu(options)
        )
    }

    // ORD API
    async syncContracts(params?: { fullScan?: boolean; provider?: string }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdSyncContracts(params as any, options)
        )
    }

    async syncCounterparties(params?: { fullScan?: boolean; provider?: string }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdSyncCounterparties(params as any, options)
        )
    }

    async syncPostsFromCreatives(params?: { provider?: string }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdSyncPostsFromCreatives(params, options)
        )
    }

    async getCounterparties(provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdCounterparties(provider ? { provider } : undefined, options)
        )
    }

    async getCounterpartyById(externalId: string, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.getCounterpartyByExternalId(externalId, provider ? { provider } : undefined, options)
        )
    }

    async getOrdPersonMy(params?: { provider?: string }) {
        return this.makeRequest(options =>
            MetkaClient.getPersonMy(params, options)
        )
    }

    async getOrdInvoices(params?: MetkaClient.GetApiV1OrdInvoicesParams) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdInvoices(params, options)
        )
    }

    async createAct(data: MetkaClient.CreateActLocalRequest, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ActsCreate(data, provider ? { provider } : undefined, options)
        )
    }

    async deleteAct(externalId: string, provider?: string) {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1ActsExternalId(externalId, provider ? { provider } : undefined, options)
        )
    }

    async batchDeleteActs(externalIds: string[], provider?: string): Promise<BatchDeleteActsResponse> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ActsBatchDelete(
                { externalIds },
                provider ? { provider } : undefined,
                options
            )
        ) as Promise<BatchDeleteActsResponse>
    }

    // ORD Providers API
    // Uses generated MetkaClient functions from metka-client.ts
    // Note: Types defined manually until backend adds [ProducesResponseType] attributes
    async getOrdProviders(): Promise<{
        providers: Array<{
            provider: string
            hasToken: boolean
            tokenCreatedAt?: string
            tokenUpdatedAt?: string
        }>
        preferredProvider?: string
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdProviders(options)
        ) as any
    }

    async getOrdProviderTokenStatus(provider: string): Promise<{
        active: boolean
        activatedAt?: string
        updatedAt?: string
        masked?: string
        provider: string
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdProvidersProviderTokenStatus(provider, options)
        ) as any
    }

    async verifyOrdProviderToken(provider: string, token: string): Promise<{
        ok: boolean
        valid: boolean
        provider: string
    }> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdProvidersProviderTokenVerify(provider, { token }, options)
        ) as any
    }

    async saveOrdProviderToken(provider: string, token: string): Promise<void> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdProvidersProviderToken(provider, { token }, options)
        ) as any
    }

    async deleteOrdProviderToken(provider: string): Promise<void> {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1OrdProvidersProviderToken(provider, options)
        ) as any
    }

    async testOrdProviderToken(provider: string): Promise<{
        ok: boolean
        valid: boolean
        provider: string
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdProvidersProviderTokenTest(provider, options)
        ) as any
    }

    async setPreferredOrdProvider(provider: string): Promise<void> {
        return this.makeRequest(options =>
            MetkaClient.putApiV1OrdProvidersPreferred({ provider }, options)
        ) as any
    }

    // Content Generation API
    async generateErid(data: MetkaClient.GenerateEridRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ContentGenerateErid(data, options)
        )
    }

    // Telegram API
    async getTelegramMe() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1TgMe(options)
        )
    }

    async publishTelegram(data: MetkaClient.PublishRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1TgPublish(data, options)
        )
    }

    /**
     * Publish post using unified endpoint that routes to correct platform (Telegram/VK).
     */
    async publishByPost(postId: string, opts?: { publishAtMs?: number; deleteAtMs?: number }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1Publish({
                postId,
                publishAtMs: opts?.publishAtMs ?? null,
                deleteAtMs: opts?.deleteAtMs ?? null,
            }, options)
        )
    }

    async getTelegramMessages(erid: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1TgMessages({ erid }, options)
        )
    }

    async getTelegramMetrics(erid: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1TgMetrics({ erid }, options)
        )
    }

    async collectTelegramMetricsNow(erid?: string) {
        return this.makeRequest(async () => {
            const headers = await this.getAuthHeaders()
            const url = erid ? `/api/metrics/collect-now?erid=${encodeURIComponent(erid)}` : `/api/metrics/collect-now`
            const resp = await fetch(url, { method: 'POST', headers })
            await ensureOk(resp)
            try { return await resp.json() } catch { return { ok: true } }
        })
    }

    // Пользовательский сбор метрик для своих постов (без админских прав)
    async collectTelegramMetricsMine(erid?: string) {
        return this.makeRequest(async () => {
            const headers = await this.getAuthHeaders()
            const qs = erid ? `?erid=${encodeURIComponent(erid)}` : ''
            const resp = await fetch(`/api/v1/tg/metrics/collect-mine${qs}`,
                { method: 'POST', headers })
            await ensureOk(resp)
            try { return await resp.json() } catch { return { ok: true } }
        })
    }

    async updateTelegramAutodelete(postId: string | number, deleteAtMs: number) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1PostsIdTgAutodelete(String(postId), { deleteAtMs }, options)
        )
    }

    // Storage API
    async presignUpload(data: MetkaClient.PresignUploadRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1StoragePresignUpload(data, options)
        )
    }

    async presignUploadProcessed(data: MetkaClient.PresignUploadProcessedRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1StoragePresignUploadProcessed(data, options)
        )
    }

    async uploadProcessedFile(formData: FormData) {
        // Полностью полагаемся на BFF прокси /api/v1/*, который добавляет Authorization на сервере
        const response = await fetch('/api/v1/storage/upload-processed', {
            method: 'POST',
            body: formData,
        })
        await ensureOk(response)
        return response.json()
    }

    async finalizeCreative(data: MetkaClient.FinalizeCreativeRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1StorageFinalizeCreative(data, options)
        )
    }

    // Video Storage API - generated from backend
    async presignUploadVideo(data: MetkaClient.PresignUploadVideoRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1StoragePresignUploadVideo(data, options)
        )
    }

    async finalizeVideo(data: MetkaClient.FinalizeVideoRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1StorageFinalizeVideo(data, options)
        )
    }

    async presignGet(params: MetkaClient.GetApiV1StoragePresignGetParams) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1StoragePresignGet(params, options)
        )
    }

    // Note: debugListByPost removed - endpoint doesn't exist in backend

    // Jobs API
    async getJob(id: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1JobsId(id, options)
        )
    }

    async getJobEvents(id: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1JobsIdEvents(id, options)
        )
    }

    // Submit API
    async submit() {
        return this.makeRequest(options =>
            MetkaClient.postApiV1Submit(options)
        )
    }

    async submitArchive(data: MetkaClient.ArchiveSubmitRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1SubmitArchive(data, options)
        )
    }

    // Export API
    async exportArchives(params?: MetkaClient.ExportArchivesParams) {
        return this.makeRequest(options =>
            MetkaClient.exportArchives(params, options)
        )
    }

    // Account API
    async changePassword(data: MetkaClient.ChangePasswordRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1AccountChangePassword(data, options)
        )
    }

    async createAccount(data: MetkaClient.CreateAccountRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1AccountCreate(data, options)
        )
    }

    async getAccounts() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Accounts(options)
        )
    }

    async switchAccount(id: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1AccountsIdSwitch(id, options)
        )
    }

    async createAdditionalAccount(data: { name: string }) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1AccountsCreate(data, options)
        )
    }

    async renameAccount(id: string, data: { name: string }) {
        return this.makeRequest(options =>
            MetkaClient.patchApiV1AccountsId(id, data, options)
        )
    }

    async archiveAccount(id: string) {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1AccountsId(id, options)
        )
    }

    // Admin API
    async getAdminPosts(params?: MetkaClient.GetApiV1AdminPostsParams) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1AdminPosts(params, options)
        )
    }

    async adminEmailBroadcast(data: MetkaClient.BroadcastEmailRequest) {
        return this.makeRequest(options =>
            MetkaClient.adminEmailBroadcast(data, options)
        )
    }

    async bulkDeletePosts(data: MetkaClient.BulkDeleteRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1AdminPostsDelete(data, options)
        )
    }

    async getAdminSettings() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1AdminSettings(options)
        )
    }

    async setAdminSetting(data: MetkaClient.SetSettingReq) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1AdminSettings(data, options)
        )
    }

    // ORD Reach API
    async getOrdReachMonth(month: string, params?: MetkaClient.GetApiV1OrdReachMonthParams) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdReachMonth(month, params, options)
        )
    }

    async getOrdReachMonthMissing(month: string, params?: MetkaClient.GetApiV1OrdReachMonthMissingParams) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdReachMonthMissing(month, params, options)
        )
    }

    async getOrdReachAllowedMonths() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdReachAllowedMonths(options)
        )
    }

    async submitOrdReach(data: MetkaClient.ReachSubmitRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdReachSubmit(data, options)
        )
    }

    // Reach Drafts API
    async getOrdReachDraft(month: string) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1OrdReachDraftMonth(month, options)
        )
    }

    // buildOrdReachDraft removed from API
    // async buildOrdReachDraft(month: string) {
    //     return this.makeRequest(options =>
    //         MetkaClient.postApiV1OrdReachDraftMonthBuild(month, options)
    //     )
    // }

    async saveOrdReachDraft(month: string, payload: any) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdReachDraftsMonth(month, payload, options)
        )
    }

    async submitOrdReachDraft(month: string) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1OrdReachDraftsMonthSubmit(month, options)
        )
    }

    // Examples API
    async getExamples() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1Examples(options)
        )
    }

    async createExample(data: MetkaClient.ExampleCreateRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1Examples(data, options)
        )
    }

    async deleteExamples() {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1Examples(options)
        )
    }

    async getExample(id: number) {
        return this.makeRequest(options =>
            MetkaClient.getApiV1ExamplesId(id, options)
        )
    }

    // Settings API
    async getPublicSettings() {
        return this.makeRequest(options =>
            MetkaClient.getApiV1SettingsPublic(options)
        )
    }

    // Compliance Audit API
    async getComplianceAudit(creativeId: string): Promise<{ items: Array<{ kind: string; requestId?: string; finalStatus: string; createdAt: string; durationMs?: number }> }> {
        const headers = await this.getAuthHeaders()
        const resp = await fetch(`/api/v1/compliance/audit?creativeId=${encodeURIComponent(creativeId)}`, {
            method: 'GET',
            headers,
            credentials: 'include',
        })
        await ensureOk(resp)
        return resp.json()
    }

    // Submit Analysis API (via BFF route /api/submit)
    async submitSinglePost(payload: {
        body: {
            type: string
            object: {
                id: number
                date: number
                text: string
                attachments?: any[]
            }
        }
        authorName?: string
        meta?: { placementContext?: string; flags?: Record<string, boolean> }
    }): Promise<{ data: any; resultId?: string }> {
        const headers = await this.getAuthHeaders()
        const response = await fetch('/api/submit', {
            method: 'POST',
            headers: {
                ...headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        })

        const data = await response.json()
        const resultId = response.headers.get('x-analysis-result-id') || undefined

        return { data, resultId }
    }

    // Health API
    async getHealth() {
        return this.makeRequest(options =>
            MetkaClient.getHealth(options)
        )
    }

    async getHealthDb() {
        return this.makeRequest(options =>
            MetkaClient.getHealthDb(options)
        )
    }

    // ORD Status API (via BFF route)
    async getOrdStatus(params?: { limit?: number }): Promise<{ items: Array<{ status: string; updatedAt: number; erid: string | null }> }> {
        const headers = await this.getAuthHeaders()
        const qs = new URLSearchParams()
        if (params?.limit) qs.set('limit', String(params.limit))
        const url = `/api/ord/status${qs.toString() ? '?' + qs.toString() : ''}`
        const resp = await fetch(url, {
            method: 'GET',
            headers,
            cache: 'no-store',
        })
        await ensureOk(resp)
        return resp.json()
    }

    // INN Lookup API
    async lookupInn(data: MetkaClient.InnLookupRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1InnLookup(data, options)
        )
    }

    // User management API
    async deleteUser() {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1UsersMe(options)
        )
    }

    // Public API (landing forms)
    async submitFeedback(data: MetkaClient.FeedbackRequest) {
        return this.makeRequest(() =>
            MetkaClient.submitFeedback(data)
        )
    }

    // Admin API (not fully documented in OpenAPI yet)
    async getAdminRoles() {
        return this.makeRequest(options =>
            fetch('/api/admin/roles', { ...options, method: 'GET' }).then(r => r.json())
        )
    }

    async createUser(data: any) {
        return this.makeRequest(options =>
            fetch('/api/admin/users/create', {
                ...options,
                method: 'POST',
                body: JSON.stringify(data),
            }).then(r => r.json())
        )
    }

    async createAdminUser(data: any) {
        return this.makeRequest(options =>
            fetch('/api/admin/users/create-admin', {
                ...options,
                method: 'POST',
                body: JSON.stringify(data),
            }).then(r => r.json())
        )
    }

    async getUserRoles(userId: string) {
        return this.makeRequest(options =>
            fetch(`/api/admin/users/${encodeURIComponent(userId)}/roles`, options).then(r => r.json())
        )
    }

    async grantUserRoles(userId: string, roles: string[]): Promise<boolean> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/roles`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ roles }),
            })
            const data = await resp.json().catch(() => ({ ok: false }))
            return Boolean(data?.ok)
        })
    }

    async revokeUserRoles(userId: string, roles: string[]): Promise<boolean> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/roles`, {
                method: 'DELETE',
                headers,
                body: JSON.stringify({ roles }),
            })
            const data = await resp.json().catch(() => ({ ok: false }))
            return Boolean(data?.ok)
        })
    }

    async getAdminUsers(params: { q?: string; page?: number; size?: number }): Promise<{ ok: boolean; items: any[]; page: number; size: number }> {
        return this.makeRequest(async (options) => {
            const url = new URL('/api/admin/users', typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
            if (params.q) url.searchParams.set('q', params.q)
            if (params.page !== undefined) url.searchParams.set('page', String(params.page))
            if (params.size !== undefined) url.searchParams.set('size', String(params.size))
            const resp = await fetch(url.toString(), { ...options, cache: 'no-store' })
            const data = await resp.json()
            if (!resp.ok || !data?.ok) throw new Error(data?.error || 'fetch_failed')
            return data as { ok: true; items: any[]; page: number; size: number }
        })
    }

    async getUserRolesBatch(userIds: string[]): Promise<Record<string, { name: string }[]>> {
        if (!userIds.length) return {}
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch('/api/admin/users/roles/batch', {
                method: 'POST',
                headers,
                body: JSON.stringify({ userIds }),
            })
            const data = await resp.json().catch(() => ({ ok: false }))
            if (!resp.ok || !data?.ok || typeof data?.items !== 'object') return {}
            const raw = data.items as Record<string, any>
            const map: Record<string, { name: string }[]> = Object.fromEntries(
                Object.entries(raw).map(([k, v]) => [
                    k,
                    Array.isArray(v)
                        ? (v as any[]).map(x => ({ name: String((x as any)?.name || '') })).filter(x => x.name)
                        : [],
                ])
            )
            return map
        })
    }

    async uploadKktuFile(formData: FormData) {
        return this.makeRequest(async (options) => {
            // Remove explicit Content-Type to allow browser to set multipart boundary
            const rawHeaders = (options?.headers ?? {}) as Record<string, string>
            const headers: Record<string, string> = {}
            for (const [k, v] of Object.entries(rawHeaders)) {
                if (k.toLowerCase() !== 'content-type') headers[k] = v
            }

            const response = await fetch('/api/admin/kktu/upload', {
                method: 'POST',
                headers,
                body: formData,
            })

            let data: any = null
            try { data = await response.json() } catch { /* keep null */ }

            if (!response.ok || (data && typeof data === 'object' && 'error' in data)) {
                const message = (data && typeof data === 'object' && (data as any).error) || 'upload_failed'
                throw new Error(String(message))
            }

            return data
        })
    }

    // Partner API
    async activatePartner(consentVersion: string) {
        return this.makeRequest(options =>
            MetkaClient.activatePartner({ consentVersion }, options)
        )
    }

    // Partner payouts (not yet in OpenAPI client)
    async requestPartnerPayout(idempotencyKey?: string) {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            // Пробрасываем Authorization если есть
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            if (idempotencyKey && idempotencyKey.length > 0) {
                headers['X-Idempotency-Key'] = idempotencyKey
            } else {
                // Простой генератор идемпотентного ключа
                headers['X-Idempotency-Key'] = `payout_${Date.now()}_${Math.random().toString(36).slice(2)}`
            }

            const resp = await fetch('/api/v1/partners/payouts/request', {
                method: 'POST',
                headers,
            })
            await ensureOk(resp)
            try { return await resp.json() } catch { return { created: true } }
        })
    }

    // Admin: pending partner payouts (not yet in OpenAPI client)
    async getAdminPendingPayouts(limit: number = 100) {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const url = `/api/v1/admin/partner/payouts/pending?limit=${encodeURIComponent(String(limit))}`
            const resp = await fetch(url, { method: 'GET', headers })
            await ensureOk(resp)
            return resp.json()
        })
    }

    // Admin: partner statistics (not yet in OpenAPI client)
    async getAdminPartnerStatistics(): Promise<Array<{
        partnerId: string
        userId: string
        email: string | null
        promoCode: string
        status: string
        activatedAt: string | null
        createdAt: string
        totalReferredClients: number
        activeSubscriptions: number
        totalRevenue: number
        totalCommissionsAccrued: number
        pendingCommissions: number
        paidCommissions: number
        currentBalance: number
        lifetimeEarnings: number
        totalPayouts: number
        payoutCount: number
        level: string
        commissionRate: number
    }>> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch('/api/v1/admin/partner/statistics', {
                method: 'GET',
                headers,
                credentials: 'include',
            })
            await ensureOk(resp)
            return resp.json()
        })
    }

    // Admin: partner referrals (not yet in OpenAPI client)
    async getAdminPartnerReferrals(partnerId: string, page: number = 1, pageSize: number = 10): Promise<{
        referrals: Array<{
            bindingId: string
            clientUserId: string
            clientEmail: string | null
            source: string
            boundAt: string
            firstSeenAt: string
            activeSubscriptions: number
            totalSubscriptions: number
            totalRevenue: number
            paymentCount: number
        }>
        totalCount: number
    }> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const url = `/api/v1/admin/partner/${encodeURIComponent(partnerId)}/referrals?page=${page}&pageSize=${pageSize}`
            const resp = await fetch(url, {
                method: 'GET',
                headers,
                credentials: 'include',
            })
            await ensureOk(resp)
            return resp.json()
        })
    }

    // Admin: delete partner binding (not yet in OpenAPI client)
    async deleteAdminPartnerBinding(bindingId: string): Promise<void> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch(`/api/v1/admin/partner/binding/${encodeURIComponent(bindingId)}`, {
                method: 'DELETE',
                headers,
                credentials: 'include',
            })
            await ensureOk(resp)
        })
    }

    // Referral claim API (uses Next.js API route that handles cookie deletion)
    async claimReferral(): Promise<{ ok: boolean }> {
        const response = await fetch('/api/referrals/claim', {
            method: 'POST',
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(`Claim request failed: ${response.status}`)
        }

        return response.json()
    }

    // YouTube API - video linking and metrics
    async linkYouTubeVideo(postId: string, videoUrl: string): Promise<{
        videoId?: string
        videoUrl?: string
        title?: string
        channelTitle?: string
        linkedAtMs?: number
    }> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1YoutubePostsPostIdLink(postId, { videoUrl }, options)
        )
    }

    async unlinkYouTubeVideo(postId: string): Promise<void> {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1YoutubePostsPostIdLink(postId, options)
        )
    }

    async getYouTubeVideoStats(postId: string): Promise<{
        videoId?: string
        videoUrl?: string
        views?: number
        likes?: number
        comments?: number
        status?: string
        collectedAtMs?: number
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1YoutubePostsPostIdStats(postId, options)
        )
    }

    async getYouTubeReachLatest(erid: string): Promise<{
        erid?: string
        reach?: number
        updatedAtMs?: number
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1YoutubeReachEridEridLatest(erid, options)
        )
    }

    async getYouTubeReachMonthly(erid: string): Promise<{
        erid?: string
        months?: Array<{ month?: string; views?: number }>
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1YoutubeReachEridEridMonthly(erid, options)
        )
    }

    async collectYouTubeMetricsNow(): Promise<{ ok: boolean }> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1YoutubeMetricsCollectNow(options)
        )
    }

    // RuTube API — link/unlink/stats (через сгенерированный Orval клиент)
    async linkRuTubeVideo(postId: string, videoUrl: string): Promise<{
        videoId?: string
        videoUrl?: string
        title?: string | null
        linkedAtMs?: number
    }> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1RutubePostsPostIdLink(postId, { videoUrl }, options)
        )
    }

    async unlinkRuTubeVideo(postId: string): Promise<void> {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1RutubePostsPostIdLink(postId, options)
        )
    }

    async getRuTubeVideoStats(postId: string): Promise<{
        videoId?: string
        videoUrl?: string
        views?: number
        collectedAtMs?: number
        status?: string
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1RutubePostsPostIdStats(postId, options)
        )
    }

    async getRuTubeReachLatest(erid: string): Promise<{
        erid?: string
        reach?: number
        updatedAtMs?: number
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1RutubeReachEridEridLatest(erid, options)
        )
    }

    async getRuTubeReachMonthly(erid: string): Promise<{
        erid?: string
        months?: Array<{ month?: string; views?: number }>
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1RutubeReachEridEridMonthly(erid, options)
        )
    }

    async collectRuTubeMetricsNow(): Promise<{ ok: boolean }> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1RutubeMetricsCollectNow(options)
        )
    }

    // MAX (maxstat.ru) API — link/unlink/stats
    async linkMaxPost(postId: string, postUrl: string): Promise<{
        postId?: number
        postUrl?: string
        channel?: string
        slug?: string
        views?: number
        linkedAtMs?: number
    }> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1MaxPostsPostIdLink(postId, { postUrl }, options)
        )
    }

    async unlinkMaxPost(postId: string): Promise<void> {
        return this.makeRequest(options =>
            MetkaClient.deleteApiV1MaxPostsPostIdLink(postId, options)
        )
    }

    async getMaxPostStats(postId: string): Promise<{
        postId?: number
        postUrl?: string
        views?: number
        likes?: number
        collectedAtMs?: number
        status?: string
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1MaxPostsPostIdStats(postId, options)
        )
    }

    async getMaxReachLatest(erid: string): Promise<{
        erid?: string
        reach?: number
        updatedAtMs?: number
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1MaxReachEridEridLatest(erid, options)
        )
    }

    async getMaxReachMonthly(erid: string): Promise<{
        erid?: string
        months?: Array<{ month?: string; views?: number }>
    }> {
        return this.makeRequest(options =>
            MetkaClient.getApiV1MaxReachEridEridMonthly(erid, options)
        )
    }

    async collectMaxMetricsNow(): Promise<{ ok: boolean }> {
        return this.makeRequest(options =>
            MetkaClient.postApiV1MaxMetricsCollectNow(options)
        )
    }

    // VK Autopublish API - delegates to generated client
    // Note: OAuth methods (getVkConnectUrl, exchangeVkCode, finalizeVkConnection) are disabled in backend

    async publishVk(data: {
        ordPadId: string
        erid: string
        text?: string
        photos?: { sourceUrl: string }[]
        publishAt?: string
    }, idempotencyKey: string): Promise<{ status: 'published' | 'planned'; postId?: number; postLink?: string }> {
        const { postApiV1VkPublish } = await import('@/lib/api-client/metka-client')
        const result = await postApiV1VkPublish(
            {
                ordPadId: data.ordPadId,
                erid: data.erid,
                text: data.text,
                photos: data.photos?.map(p => ({ sourceUrl: p.sourceUrl })),
                publishAt: data.publishAt ?? null,
            },
            { 'X-Idempotency-Key': idempotencyKey }
        ) as { data: unknown; status: number }
        return result.data as any
    }

    async getVkPosts(erid: string): Promise<{
        items: Array<{
            ordPadId: string
            erid: string
            groupId: string
            postId?: number
            postLink?: string
            publishedAt?: string
            status: 'planned' | 'published' | 'deleted' | 'error'
        }>
    }> {
        const { getApiV1VkPosts } = await import('@/lib/api-client/metka-client')
        const result = await getApiV1VkPosts({ erid })
        return result.data as any
    }

    async getVkMetrics(erid: string): Promise<{
        items: Array<{
            postId: number
            views: number
            reachTotal?: number
            reachSubscribers?: number
            collectedAt: string
        }>
        aggregates: Array<{
            erid: string
            day: string
            views: number
            reachTotal?: number
            lastMeasuredAt: string
        }>
    }> {
        const { getApiV1VkMetrics } = await import('@/lib/api-client/metka-client')
        const result = await getApiV1VkMetrics({ erid })
        return result.data as any
    }

    // Admin Audit Activity Stats API
    async getAuditActivityStats(params: { interval: string; from?: string; to?: string }): Promise<{
        intervals: Array<{ timestamp: string; eventCount: number; uniqueUsers: number }>
        totalEvents: number
        totalUniqueUsers: number
        from: string
        to: string
        interval: string
    }> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const qs = new URLSearchParams({ interval: params.interval })
            if (params.from) qs.append('from', params.from)
            if (params.to) qs.append('to', params.to)
            const resp = await fetch(`/api/v1/admin/audit/activity?${qs.toString()}`, {
                method: 'GET',
                headers,
                credentials: 'include',
            })
            await ensureOk(resp)
            return resp.json()
        })
    }

    // Admin KKTU API (for admin panel)
    async getAdminKktu(params: { search?: string; limit?: number; leafOnly?: boolean }): Promise<{ items: Array<{ code: string; name: string }>; total: number }> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const qs = new URLSearchParams()
            if (params.search) qs.set('search', params.search)
            if (params.limit) qs.set('limit', String(params.limit))
            if (params.leafOnly) qs.set('leafOnly', 'true')
            const resp = await fetch(`/api/admin/kktu?${qs.toString()}`, {
                method: 'GET',
                headers,
                cache: 'no-store',
            })
            await ensureOk(resp)
            return resp.json()
        })
    }

    async startPostsExport(req: MetkaClient.PostsExportRequest) {
        return this.makeRequest(options =>
            MetkaClient.postApiV1ExportPosts(req, options)
        ) as Promise<{ exportId: string }>
    }

    async exportAdminKktu(leafOnly?: boolean): Promise<Blob> {
        const headers = await this.getAuthHeaders()
        const qs = new URLSearchParams()
        if (leafOnly) qs.set('leafOnly', 'true')
        const resp = await fetch(`/api/admin/kktu/export?${qs.toString()}`, {
            method: 'GET',
            headers,
            cache: 'no-store',
        })
        if (!resp.ok) throw new Error('export_failed')
        return resp.blob()
    }

    // Monitoring API
    async listMonitoringTasks(params?: MetkaClient.ListMonitoringTasksParams) {
        return this.makeRequest(async (options) => {
            const res = await MetkaClient.listMonitoringTasks(params, options)
            return res.data
        })
    }

    async createMonitoringTask(data: MetkaClient.CreateMonitoringTaskRequest) {
        return this.makeRequest(async (options) => {
            const res = await MetkaClient.createMonitoringTask(data, options)
            return res.data
        })
    }

    async deleteMonitoringTask(taskId: string) {
        return this.makeRequest(async (options) => {
            await MetkaClient.deleteMonitoringTask(taskId, options)
        })
    }

    async previewCsvImport(formData: FormData) {
        return this.makeRequest(async (options) => {
            const res = await MetkaClient.previewCsvImport({ ...options, body: formData })
            return res.data
        })
    }

    async confirmCsvImport(data: MetkaClient.CsvImportConfirmRequest) {
        return this.makeRequest(async (options) => {
            const res = await MetkaClient.confirmCsvImport(data, options)
            return res.data
        })
    }

    getDownloadMonitoringTemplateUrl() {
        return MetkaClient.getDownloadMonitoringTemplateUrl()
    }

    // Admin Dev Tools API
    async triggerDevReporting(month: string): Promise<any> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch(`/api/v1/admin/dev/trigger-reporting/${encodeURIComponent(month)}`, {
                method: 'POST',
                headers,
                credentials: 'include',
            })
            await ensureOk(resp)
            return resp.json()
        })
    }

    async triggerDevAggregation(): Promise<any> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch('/api/v1/admin/dev/trigger-aggregation', {
                method: 'POST',
                headers,
                credentials: 'include',
            })
            await ensureOk(resp)
            return resp.json()
        })
    }

    async triggerDevDraft(month: string): Promise<any> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch(`/api/v1/admin/dev/trigger-draft/${encodeURIComponent(month)}`, {
                method: 'POST',
                headers,
                credentials: 'include',
            })
            await ensureOk(resp)
            return resp.json()
        })
    }

    // Admin: assign tariff to user
    async assignUserTariff(userId: string, data: { tariffId: string; expiresAt?: string; durationDays?: number }): Promise<void> {
        return this.makeRequest(async (options) => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
            if (options?.headers) {
                const authHeader = (options.headers as Record<string, string>)['Authorization']
                if (authHeader) headers['Authorization'] = authHeader
            }
            const resp = await fetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/tariff-assignments`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify(data),
            })
            await ensureOk(resp)
        })
    }

    // ── Статистика: импорт из Ozon-xlsx ────────────────────────────────────
    // Файл уходит сырым телом (application/octet-stream) через customFetch-мутатор —
    // Orval-клиент (fetch+mutator) не умеет multipart/IFormFile. Имя файла — в query.
    async importOzonStatistics(file: File): Promise<MetkaClient.StatisticsImportResult> {
        const url = MetkaClient.getPostApiV1OrdStatisticsImportUrl({ fileName: file.name })
        const res = await customFetch<{ data: MetkaClient.StatisticsImportResult; status: number }>(
            url,
            {
                method: 'POST',
                body: file,
                headers: { 'Content-Type': 'application/octet-stream' },
            },
        )
        return res.data
    }

    async getStatisticsImports(page = 1, pageSize = 20): Promise<MetkaClient.StatisticsImportListResponse> {
        const res = await MetkaClient.getApiV1OrdStatisticsImports({ page, pageSize })
        return res.data
    }

    async getStatisticsImport(id: string): Promise<MetkaClient.StatisticsImportDetailDto> {
        const res = await MetkaClient.getApiV1OrdStatisticsImportsId(id)
        return res.data
    }

    // Скачивание ранее загруженного файла импорта. Бинарный ответ — мутатор всегда парсит
    // JSON, поэтому берём сырым fetch (тот же паттерн, что exportAdminKktu). Аккаунт-контекст
    // приходит через BFF-cookie. 404 = у старого импорта файл не сохранён.
    async downloadStatisticsImportFile(id: string): Promise<Blob> {
        const headers = await this.getAuthHeaders()
        const resp = await fetch(`/api/v1/ord/statistics/imports/${id}/file`, {
            method: 'GET',
            headers,
            credentials: 'include',
            cache: 'no-store',
        })
        if (!resp.ok) {
            throw new Error(resp.status === 404 ? 'import_file_not_found' : 'download_failed')
        }
        return resp.blob()
    }

}

// Export singleton instance
export const apiClient = new ApiClient()

// Re-export types from generated client
export type * from './api-client/metka-client'
